import * as THREE from "three";
import { BooleanResult, Polygon, Polyline, Solid, Vector3 } from "opengeometry";

import { IShape } from "../../shapes/base-type";
import { ElementType } from "../base-type";
import { Door, Window } from "../openings";
import { Opening } from "../openings/opening";
import { WallMaterial, WallOptions } from "./wall-types";
import { WallFrame, computeWallFrame } from "./wall-frame";
import { IPlaceable } from "../op-element";
import type { Vec3 } from "../../types";
import { rotatePoints, degToRad, Y_UP, centroid as centroidOf } from "../../../../../utils/transform";
import {
  signedAreaXZ as utilSignedAreaXZ,
  hasSelfIntersectionXZ as utilHasSelfIntersectionXZ,
  type Point2,
} from "../../../../../utils/polygon";

/** Optional hosting placement for PolyWall.attach* — offset is total arc-length along the polyline. */
export interface PolyWallHostPlacement {
  offset?: number;
  baseHeight?: number;
}

/** Resolution of a polyline arc-length offset into a specific segment. */
export interface PolyWallSegmentResolution {
  segmentIndex: number;
  localU: number;
  frame: WallFrame;
}

type WallPoint = [number, number, number];

type GeometryState = {
  points: WallPoint[];
  thickness: number;
  footprint: Vector3[];
  footprintHoles: Vector3[][];
  isClosed: boolean;
};

type GeometryStateResult =
  | { state: GeometryState }
  | { error: string };

const DEFAULT_POINTS: WallPoint[] = [
  [0, 0, 0],
  [1.5, 0, 0],
  [2.5, 0, 1.5],
];

const GEOMETRY_EPSILON = 1e-6;
const FOOTPRINT_AREA_EPSILON = 1e-6;

function clonePoint(point: WallPoint): WallPoint {
  return [point[0], point[1], point[2]];
}

function clonePoints(points: WallPoint[]): WallPoint[] {
  return points.map((point) => clonePoint(point));
}

function cloneVector(point: Vector3): Vector3 {
  return new Vector3(point.x, point.y, point.z);
}

function isFinitePoint(point: WallPoint): boolean {
  return Number.isFinite(point[0]) && Number.isFinite(point[1]) && Number.isFinite(point[2]);
}

function pointsMatch(a: WallPoint, b: WallPoint, epsilon = GEOMETRY_EPSILON): boolean {
  return (
    Math.abs(a[0] - b[0]) <= epsilon &&
    Math.abs(a[1] - b[1]) <= epsilon &&
    Math.abs(a[2] - b[2]) <= epsilon
  );
}

function vectorsMatch(a: Vector3, b: Vector3, epsilon = GEOMETRY_EPSILON): boolean {
  return (
    Math.abs(a.x - b.x) <= epsilon &&
    Math.abs(a.y - b.y) <= epsilon &&
    Math.abs(a.z - b.z) <= epsilon
  );
}

function pointDistanceSquared(a: WallPoint, b: WallPoint): number {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  const dz = a[2] - b[2];
  return dx * dx + dy * dy + dz * dz;
}

function vec3RingToPoint2(ring: Vector3[]): Point2[] {
  return ring.map((v) => [v.x, v.z] as Point2);
}

function signedAreaXZ(vertices: Vector3[]): number {
  return utilSignedAreaXZ(vec3RingToPoint2(vertices));
}

function sanitizeFootprintVertices(vertices: Vector3[]): Vector3[] {
  const sanitized: Vector3[] = [];

  for (const vertex of vertices) {
    const lastVertex = sanitized[sanitized.length - 1];
    if (!lastVertex || !vectorsMatch(lastVertex, vertex)) {
      sanitized.push(cloneVector(vertex));
    }
  }

  if (sanitized.length > 1 && vectorsMatch(sanitized[0], sanitized[sanitized.length - 1])) {
    sanitized.pop();
  }

  return sanitized;
}

function hasSelfIntersectionXZ(vertices: Vector3[]): boolean {
  return utilHasSelfIntersectionXZ(vec3RingToPoint2(vertices));
}

function discardGeometry(shape: { discardGeometry?: () => void } | null | undefined): void {
  shape?.discardGeometry?.();
}

function buildFootprint(points: WallPoint[], thickness: number): GeometryStateResult {
  const sourceLine = new Polyline({
    points: points.map((point) => new Vector3(point[0], point[1], point[2])),
    color: 0xffffff,
  });

  try {
    const positiveOffset = sourceLine.getOffset(thickness / 2, 50, true);
    const negativeOffset = sourceLine.getOffset(-thickness / 2, 50, true);

    if (positiveOffset.isClosed) {
      // Closed wall: the two offset rings form an annular (donut) cross-section —
      // outer ring as polygon vertices, inner ring as a hole.
      // For a CCW room boundary, getOffset(+d) returns the inward (smaller) ring and
      // getOffset(-d) returns the outward (larger) ring. We identify outer vs. inner
      // by absolute area so the assignment is correct for both CCW and CW input polygons.
      const ring1 = sanitizeFootprintVertices(positiveOffset.points.map((p) => cloneVector(p)));
      const ring2 = sanitizeFootprintVertices(negativeOffset.points.map((p) => cloneVector(p)));
      const ring1AreaAbs = Math.abs(signedAreaXZ(ring1));
      const ring2AreaAbs = Math.abs(signedAreaXZ(ring2));
      const outerRing = ring1AreaAbs >= ring2AreaAbs ? ring1 : ring2;
      const innerRing = ring1AreaAbs < ring2AreaAbs ? ring1 : ring2;

      if (outerRing.length < 3 || innerRing.length < 3) {
        return {
          error: "Invalid polyline wall geometry: Closed wall offset rings are degenerate.",
        };
      }

      if (hasSelfIntersectionXZ(outerRing) || hasSelfIntersectionXZ(innerRing)) {
        return {
          error:
            "Invalid polyline wall geometry: Closed wall offset rings self-intersect. Reduce the wall thickness or simplify the path.",
        };
      }

      const outerArea = signedAreaXZ(outerRing);
      const innerArea = signedAreaXZ(innerRing);

      if (Math.abs(outerArea) <= FOOTPRINT_AREA_EPSILON || Math.abs(innerArea) <= FOOTPRINT_AREA_EPSILON) {
        return {
          error:
            "Invalid polyline wall geometry: Closed wall is too thick — inner ring has collapsed. Reduce the wall thickness.",
        };
      }

      // Outer ring: CW (area < 0); inner ring (hole): CCW (area > 0).
      const normalizedOuter = outerArea > 0 ? outerRing.slice().reverse() : outerRing;
      const normalizedInner = innerArea < 0 ? innerRing.slice().reverse() : innerRing;

      return {
        state: {
          points: clonePoints(points),
          thickness,
          footprint: normalizedOuter.map((p) => cloneVector(p)),
          footprintHoles: [normalizedInner.map((p) => cloneVector(p))],
          isClosed: true,
        },
      };
    }

    // Open wall: merge left-forward + right-reversed into a single flat polygon.
    const footprint = sanitizeFootprintVertices([
      ...positiveOffset.points.map((point) => cloneVector(point)),
      ...negativeOffset.points.slice().reverse().map((point) => cloneVector(point)),
    ]);

    if (footprint.length < 3) {
      return {
        error: "Invalid polyline wall geometry: Offset footprint must contain at least three unique vertices.",
      };
    }

    if (hasSelfIntersectionXZ(footprint)) {
      return {
        error:
          "Invalid polyline wall geometry: Offset footprint self-intersects. Reduce the wall thickness or simplify the path.",
      };
    }

    const area = signedAreaXZ(footprint);
    if (Math.abs(area) <= FOOTPRINT_AREA_EPSILON) {
      return {
        error: "Invalid polyline wall geometry: Offset footprint area is too small to build a stable wall.",
      };
    }

    const normalizedFootprint = area > 0 ? footprint.slice().reverse() : footprint;

    return {
      state: {
        points: clonePoints(points),
        thickness,
        footprint: normalizedFootprint.map((point) => cloneVector(point)),
        footprintHoles: [],
        isClosed: false,
      },
    };
  } catch (error) {
    return {
      error: `Invalid polyline wall geometry: Failed to compute wall footprint${
        error instanceof Error && error.message ? ` (${error.message})` : "."
      }`,
    };
  } finally {
    discardGeometry(sourceLine);
  }
}

function prepareGeometryState(points: WallPoint[], thickness: number): GeometryStateResult {
  if (!Number.isFinite(thickness) || thickness <= 0) {
    return {
      error: "Invalid polyline wall geometry: Wall thickness must be a positive finite number.",
    };
  }

  if (points.length < 2) {
    return {
      error: "Invalid polyline wall geometry: A polyline wall requires at least two points.",
    };
  }

  const clonedPoints = clonePoints(points);
  const baseY = clonedPoints[0][1];

  for (let index = 0; index < clonedPoints.length; index += 1) {
    const point = clonedPoints[index];
    if (!isFinitePoint(point)) {
      return {
        error: `Invalid polyline wall geometry: Point ${index} must contain finite numbers.`,
      };
    }

    if (Math.abs(point[1] - baseY) > GEOMETRY_EPSILON) {
      return {
        error:
          "Invalid polyline wall geometry: Polyline walls currently support only horizontal paths, so all point Y values must match.",
      };
    }

    if (index > 0 && pointDistanceSquared(clonedPoints[index - 1], point) <= GEOMETRY_EPSILON * GEOMETRY_EPSILON) {
      return {
        error: `Invalid polyline wall geometry: Consecutive points at index ${index - 1} and ${index} cannot define a zero-length segment.`,
      };
    }
  }

  const isClosed = pointsMatch(clonedPoints[0], clonedPoints[clonedPoints.length - 1]);
  if (isClosed && clonedPoints.length < 4) {
    return {
      error: "Invalid polyline wall geometry: A closed polyline wall requires at least 4 points (3 unique corners plus the repeated closing point).",
    };
  }

  return buildFootprint(clonedPoints, thickness);
}

function disposeObject(obj: THREE.Object3D): void {
  const mesh = obj as THREE.Mesh;
  if (mesh.geometry) {
    mesh.geometry.dispose();
  }
  if (Array.isArray(mesh.material)) {
    mesh.material.forEach((material) => material.dispose());
  } else if (mesh.material) {
    mesh.material.dispose();
  }
  obj.removeFromParent();
}

export class PolyWall extends Polyline implements IShape, IPlaceable {
  ogType = ElementType.WALL;

  subElements2D: Map<string, THREE.Object3D> = new Map();
  private isProfileView = true;

  subElements3D: Map<string, THREE.Object3D> = new Map();
  private isModelView = true;

  private openings: Opening[] = [];
  private _openingHandlers: Map<string, () => void> = new Map();
  private _wallOutlineVertices: Vector3[] = [];
  private _wallHoleVertices: Vector3[][] = [];

  selected = false;
  edit = false;
  locked = false;

  /** Whether to keep this as pure line and not convert to 2D and 3D meshes. */
  isLineWall = false;

  _outlineEnabled = false;

  propertySet: WallOptions = {
    labelName: "Standard Polyline Wall",
    thickness: 0.3,
    material: WallMaterial.CONCRETE,
    color: 0xcccccc,
    height: 3,
    points: clonePoints(DEFAULT_POINTS),
    openings: [],
  };

  move(delta: Vec3): void {
    this.propertySet.points = this.propertySet.points.map(
      (p) => [p[0] + delta[0], p[1] + delta[1], p[2] + delta[2]] as WallPoint,
    );
    this.setOPGeometry();
  }

  rotate(angleDeg: number, axis: Vec3 = Y_UP, anchor?: Vec3): void {
    const pivot = anchor ?? centroidOf(this.propertySet.points as Vec3[]);
    const rotated = rotatePoints(
      this.propertySet.points as Vec3[],
      degToRad(angleDeg),
      axis,
      pivot,
    );
    this.propertySet.points = rotated.map((p) => [p[0], p[1], p[2]] as WallPoint);
    this.setOPGeometry();
  }

  get outline() {
    return this._outlineEnabled;
  }

  set outline(value: boolean) {
    this._outlineEnabled = value;
    this.applyRenderStyles();
  }

  get labelName() {
    return this.propertySet.labelName;
  }

  set labelName(value: string) {
    this.propertySet.labelName = value;
  }

  get wallThickness() {
    return this.propertySet.thickness;
  }

  set wallThickness(value: number) {
    const nextThickness = Math.max(0.1, value);
    const geometryState = this.resolveGeometryState(this.propertySet.points, nextThickness);
    if (!geometryState) {
      return;
    }

    this.propertySet.thickness = nextThickness;
    this.setOPGeometry(geometryState);
  }

  get wallMaterial() {
    return this.propertySet.material;
  }

  set wallMaterial(value: WallMaterial | string) {
    this.propertySet.material = value;
  }

  get wallColor() {
    return this.propertySet.color;
  }

  set wallColor(value: number) {
    this.propertySet.color = value;
    this.setOPMaterial();
  }

  get wallHeight() {
    return this.propertySet.height;
  }

  set wallHeight(value: number) {
    this.propertySet.height = Math.max(0.1, value);
    this.setOPGeometry();
  }

  get points(): WallPoint[] {
    return clonePoints(this.propertySet.points);
  }

  get start() {
    return this.propertySet.points[0];
  }

  set start(value: WallPoint) {
    this.updatePoint(0, value);
  }

  get end() {
    return this.propertySet.points[this.propertySet.points.length - 1];
  }

  set end(value: WallPoint) {
    this.updatePoint(this.propertySet.points.length - 1, value);
  }

  get profileView() {
    return this.isProfileView;
  }

  set profileView(value: boolean) {
    this.isProfileView = value;
    this.syncViewVisibility();
  }

  get modelView() {
    return this.isModelView;
  }

  set modelView(value: boolean) {
    this.isModelView = value;
    this.syncViewVisibility();
  }

  constructor(wallConfig?: Partial<WallOptions>) {
    const requestedThickness = Math.max(0.1, wallConfig?.thickness ?? 0.3);
    const requestedState = prepareGeometryState(clonePoints(wallConfig?.points ?? DEFAULT_POINTS), requestedThickness);
    const fallbackState = prepareGeometryState(clonePoints(DEFAULT_POINTS), 0.3);
    if ("error" in fallbackState) {
      throw new Error(fallbackState.error);
    }

    const initialState = "state" in requestedState ? requestedState.state : fallbackState.state;

    if ("error" in requestedState) {
      console.error(`${requestedState.error} Falling back to the default polyline wall path.`);
    }

    super({
      ogid: wallConfig?.ogid,
      points: initialState.points.map((point) => new Vector3(point[0], point[1], point[2])),
      color: wallConfig?.color ?? 0xcccccc,
    });

    this.subElements2D = new Map<string, THREE.Object3D>();
    this.subElements3D = new Map<string, THREE.Object3D>();

    if (wallConfig) {
      this.propertySet = {
        ...this.propertySet,
        ...wallConfig,
        openings: [...(wallConfig.openings ?? this.propertySet.openings)],
      };
    }

    this.propertySet.points = clonePoints(initialState.points);
    this.propertySet.thickness = initialState.thickness;
    this.propertySet.ogid = this.ogid;

    this.setOPGeometry(initialState);
  }

  setOPConfig(config: WallOptions): void {
    const nextThickness = Math.max(0.1, config.thickness ?? this.propertySet.thickness);
    const nextPoints = clonePoints(config.points ?? this.propertySet.points);
    const geometryState = this.resolveGeometryState(nextPoints, nextThickness);
    if (!geometryState) {
      return;
    }

    this.propertySet = {
      ...this.propertySet,
      ...config,
      thickness: nextThickness,
      points: clonePoints(geometryState.points),
      openings: [...(config.openings ?? this.propertySet.openings)],
    };

    this.setOPGeometry(geometryState);
    this.setOPMaterial();
  }

  getOPConfig(): WallOptions {
    return this.propertySet;
  }

  setPoints(points: WallPoint[]): boolean {
    const geometryState = this.resolveGeometryState(points, this.propertySet.thickness);
    if (!geometryState) {
      return false;
    }

    this.propertySet.points = clonePoints(geometryState.points);
    this.setOPGeometry(geometryState);
    return true;
  }

  appendPoint(point: WallPoint): boolean {
    const nextPoints = this.points;
    nextPoints.push(clonePoint(point));
    return this.setPoints(nextPoints);
  }

  insertPoint(index: number, point: WallPoint): boolean {
    if (index < 0 || index > this.propertySet.points.length) {
      console.error("Invalid polyline wall geometry: Insert index is out of range.");
      return false;
    }

    const nextPoints = this.points;
    nextPoints.splice(index, 0, clonePoint(point));
    return this.setPoints(nextPoints);
  }

  updatePoint(index: number, point: WallPoint): boolean {
    if (index < 0 || index >= this.propertySet.points.length) {
      console.error("Invalid polyline wall geometry: Point index is out of range.");
      return false;
    }

    const nextPoints = this.points;
    nextPoints[index] = clonePoint(point);
    return this.setPoints(nextPoints);
  }

  removePoint(index: number): boolean {
    if (index < 0 || index >= this.propertySet.points.length) {
      console.error("Invalid polyline wall geometry: Point index is out of range.");
      return false;
    }

    const nextPoints = this.points;
    nextPoints.splice(index, 1);
    return this.setPoints(nextPoints);
  }

  /**
   * Resolve an arc-length offset along the polyline into the segment that
   * owns it, plus the wall-local u distance within that segment and the
   * segment's WallFrame. Throws if the polyline has fewer than 2 points
   * or the offset is out of range.
   */
  getFrameForOffset(offset: number): PolyWallSegmentResolution {
    const pts = this.propertySet.points;
    if (pts.length < 2) {
      throw new Error("PolyWall.getFrameForOffset: polyline must have at least 2 points.");
    }

    let remaining = offset;
    for (let i = 0; i < pts.length - 1; i += 1) {
      const a = new Vector3(pts[i][0], pts[i][1], pts[i][2]);
      const b = new Vector3(pts[i + 1][0], pts[i + 1][1], pts[i + 1][2]);
      const segLength = a.clone().subtract(b).length();
      if (remaining <= segLength || i === pts.length - 2) {
        const frame = computeWallFrame(a, b, this.propertySet.thickness);
        const localU = Math.max(0, Math.min(remaining, segLength));
        return { segmentIndex: i, localU, frame };
      }
      remaining -= segLength;
    }

    throw new Error("PolyWall.getFrameForOffset: unreachable offset resolution.");
  }

  private applyHostPlacement(element: Door | Window | Opening, placement?: PolyWallHostPlacement): WallFrame {
    const offset = placement?.offset ?? 0;
    const baseHeight = placement?.baseHeight ?? 0;
    const resolution = this.getFrameForOffset(offset);

    // Mutate the propertySet directly so the station change does NOT trigger
    // a setOPGeometry (which would rebuild against the OLD host frame and
    // immediately get thrown away by the bindHostFrame call below). The
    // bindHostFrame call then performs a single rebuild against the new
    // frame with the new station already in place.
    if (element instanceof Door) {
      element.propertySet.stationLocal = { alongWall: resolution.localU, elevation: baseHeight };
    } else if (element instanceof Window) {
      element.propertySet.stationLocal = { alongWall: resolution.localU, elevation: baseHeight };
    }

    element.bindHostFrame(resolution.frame);
    return resolution.frame;
  }

  attachDoor(doorElement: Door, placement?: PolyWallHostPlacement) {
    doorElement.hostWallId = this.ogid;
    this.applyHostPlacement(doorElement, placement);

    const openingFromDoor = doorElement.opening as Opening;
    if (!openingFromDoor) {
      console.error("Door element does not have a valid opening configuration.");
      return;
    }

    this.openings.push(openingFromDoor);
    const openingConfig = openingFromDoor.getOPConfig();
    this.propertySet.openings.push(openingConfig.ogid!);
    this.resolveOpenings();

    const handler = () => this.resolveOpenings();
    this._openingHandlers.set(openingFromDoor.ogid, handler);
    openingFromDoor.onOpeningUpdated.add(handler);
  }

  /**
   * Detach a previously attached door. Removes it from the openings list,
   * unbinds its host frame (so it falls back to the unhosted identity frame),
   * and re-resolves the wall's CSG. Mirrors SingleWall.detachDoor.
   */
  detachDoor(doorElement: Door) {
    const openingFromDoor = doorElement.opening as Opening;
    const openingOgid = openingFromDoor?.ogid;

    const index = this.openings.findIndex((o) => o === openingFromDoor);
    if (index !== -1) this.openings.splice(index, 1);

    if (openingOgid) {
      const propIndex = this.propertySet.openings.indexOf(openingOgid);
      if (propIndex !== -1) this.propertySet.openings.splice(propIndex, 1);

      const handler = this._openingHandlers.get(openingOgid);
      if (handler) {
        openingFromDoor.onOpeningUpdated.remove(handler);
        this._openingHandlers.delete(openingOgid);
      }
    }

    doorElement.hostWallId = undefined;
    doorElement.bindHostFrame(null);

    this.resolveOpenings();
  }

  /**
   * Detach a previously attached window. Removes it from the openings list,
   * unbinds its host frame (so it falls back to the unhosted identity frame),
   * and re-resolves the wall's CSG. Mirrors detachDoor and SingleWall.detachWindow.
   */
  detachWindow(windowElement: Window) {
    const openingFromWindow = windowElement.opening as Opening;
    const openingOgid = openingFromWindow?.ogid;

    const index = this.openings.findIndex((o) => o === openingFromWindow);
    if (index !== -1) this.openings.splice(index, 1);

    if (openingOgid) {
      const propIndex = this.propertySet.openings.indexOf(openingOgid);
      if (propIndex !== -1) this.propertySet.openings.splice(propIndex, 1);

      const handler = this._openingHandlers.get(openingOgid);
      if (handler) {
        openingFromWindow.onOpeningUpdated.remove(handler);
        this._openingHandlers.delete(openingOgid);
      }
    }

    windowElement.hostWallId = undefined;
    windowElement.bindHostFrame(null);

    this.resolveOpenings();
  }

  attachWindow(windowElement: Window, placement?: PolyWallHostPlacement) {
    windowElement.hostWallId = this.ogid;
    this.applyHostPlacement(windowElement, placement);

    const openingFromWindow = windowElement.opening as Opening;
    if (!openingFromWindow) {
      console.error("Window element does not have a valid opening configuration.");
      return;
    }

    this.openings.push(openingFromWindow);
    const openingConfig = openingFromWindow.getOPConfig();
    this.propertySet.openings.push(openingConfig.ogid!);
    this.resolveOpenings();

    const handler = () => this.resolveOpenings();
    this._openingHandlers.set(openingFromWindow.ogid, handler);
    openingFromWindow.onOpeningUpdated.add(handler);
  }

  moveDoor(doorElement: Door, placement?: PolyWallHostPlacement) {
    this.applyHostPlacement(doorElement, placement);
    this.resolveOpenings();
  }

  moveWindow(windowElement: Window, placement?: PolyWallHostPlacement) {
    this.applyHostPlacement(windowElement, placement);
    this.resolveOpenings();
  }

  attachOpening(openingElement: Opening, placement?: PolyWallHostPlacement) {
    this.applyHostPlacement(openingElement, placement);

    this.openings.push(openingElement);
    const openingConfig = openingElement.getOPConfig();
    this.propertySet.openings.push(openingConfig.ogid!);
    this.resolveOpenings();

    const handler = () => this.resolveOpenings();
    this._openingHandlers.set(openingElement.ogid, handler);
    openingElement.onOpeningUpdated.add(handler);
  }

  detachOpening(ogid: string) {
    const index = this.openings.findIndex((opening) => opening.ogid === ogid);
    if (index === -1) {
      return;
    }

    const opening = this.openings[index];
    this.openings.splice(index, 1);
    this.propertySet.openings = this.propertySet.openings.filter((openingId) => openingId !== ogid);

    const handler = this._openingHandlers.get(ogid);
    if (handler) {
      opening.onOpeningUpdated.remove(handler);
      this._openingHandlers.delete(ogid);
    }

    this.resolveOpenings();
  }

  getHostedOpenings() {
    return this.openings;
  }

  resolveOpenings() {
    const wall3D = this.subElements3D.get(this.ogid + "-3d-base") as Solid | undefined;

    // ── 2D: rebuild polygon-with-holes ────────────────────────────────────────
    this.clearResolvedElement(this.subElements2D, this.ogid + "-2d");
    this.clearResolvedElement(this.subElements3D, this.ogid + "-3d-resolved");

    if (this._wallOutlineVertices.length >= 3) {
      const holeLoops = this.openings
        .map((opening) => opening.holeLoop2D)
        .filter((loop): loop is Vector3[] => loop.length >= 3);

      const wall2D = new Polygon({
        vertices: this._wallOutlineVertices.map((point) => cloneVector(point)),
        holes: [
          ...this._wallHoleVertices.map((ring) => ring.map((point) => cloneVector(point))),
          ...holeLoops,
        ],
        color: this.propertySet.color,
      });
      this.subElements2D.set(this.ogid + "-2d", wall2D);
      this.add(wall2D);
    }

    // ── 3D: unchanged boolean subtract ────────────────────────────────────────
    const all3DOpenings = this.openings
      .map((opening) => opening.opening3D)
      .filter((opening): opening is Solid => Boolean(opening));

    if (wall3D && all3DOpenings.length > 0) {
      try {
        const result3D = wall3D.subtract(all3DOpenings, {
          color:   this.propertySet.color,
          outline: this._outlineEnabled,
        }) as BooleanResult;

        this.subElements3D.set(this.ogid + "-3d-resolved", result3D);
        this.add(result3D);
      } catch (error) {
        console.error(
          `[PolyWall] 3D subtract failed for ${all3DOpenings.length} cutter(s).`,
          "openings:", this.openings.map((o) => o.ogid),
          error,
        );
        throw error;
      }
    }

    all3DOpenings.forEach((opening3D) => {
      opening3D.visible = false;
    });

    this.applyRenderStyles();
    this.syncViewVisibility();
  }

  dispose() {
    for (const obj of this.subElements2D.values()) {
      disposeObject(obj);
    }

    for (const obj of this.subElements3D.values()) {
      disposeObject(obj);
    }

    this.subElements2D.clear();
    this.subElements3D.clear();
    this.discardGeometry();
  }

  setOPGeometry(prevalidatedGeometry?: GeometryState): void {
    const geometryState = prevalidatedGeometry ?? this.resolveGeometryState(this.propertySet.points, this.propertySet.thickness);
    if (!geometryState) {
      return;
    }

    this.dispose();

    this.setConfig({
      points: geometryState.points.map((point) => new Vector3(point[0], point[1], point[2])),
      color: this.propertySet.color,
    });

    if (this.isLineWall) {
      return;
    }

    this._wallOutlineVertices = geometryState.footprint.map((point) => cloneVector(point));
    this._wallHoleVertices = (geometryState.footprintHoles ?? []).map((ring) =>
      ring.map((point) => cloneVector(point)),
    );

    // Base polygon: extrusion seed for the 3D path; not directly rendered (resolveOpenings builds '-2d').
    const polygon = new Polygon({
      ogid: this.ogid + "-2d-base",
      vertices: this._wallOutlineVertices.map((point) => cloneVector(point)),
      holes: this._wallHoleVertices.map((ring) => ring.map((point) => cloneVector(point))),
      color: this.propertySet.color,
    });
    polygon.visible = false;
    this.subElements2D.set(polygon.ogid, polygon);
    this.add(polygon);

    const extrudedShape = polygon.extrude(this.propertySet.height);
    extrudedShape.ogid = this.ogid + "-3d-base";
    extrudedShape.color = this.propertySet.color;
    this.subElements3D.set(extrudedShape.ogid, extrudedShape);
    this.add(extrudedShape);

    this.resolveOpenings();
    this.applyRenderStyles();
    this.syncViewVisibility();
  }

  setOPMaterial(): void {
    this.color = this.propertySet.color;
    this.applyRenderStyles();
  }

  private resolveGeometryState(points: WallPoint[], thickness: number): GeometryState | null {
    const result = prepareGeometryState(points, thickness);
    if ("error" in result) {
      console.error(result.error);
      return null;
    }

    return result.state;
  }

  private clearResolvedElement(store: Map<string, THREE.Object3D>, key: string): void {
    const existing = store.get(key);
    if (!existing) {
      return;
    }

    disposeObject(existing);
    store.delete(key);
  }

  private syncViewVisibility(): void {
    const wall2DKey     = this.ogid + "-2d";
    const resolved3DKey = this.ogid + "-3d-resolved";
    const hasResolved3D = this.subElements3D.has(resolved3DKey);

    for (const [key, obj] of this.subElements2D.entries()) {
      // '-2d-base' is the extrusion seed, never shown; '-2d' is the visible plan polygon.
      obj.visible = this.isProfileView && key === wall2DKey;
    }

    for (const [key, obj] of this.subElements3D.entries()) {
      obj.visible = this.isModelView && (!hasResolved3D || key === resolved3DKey);
    }
  }

  private applyRenderStyles(): void {
    for (const obj of this.subElements2D.values()) {
      this.apply2DRenderStyle(obj);
    }

    for (const obj of this.subElements3D.values()) {
      this.apply3DRenderStyle(obj);
    }
  }

  private apply2DRenderStyle(obj: THREE.Object3D): void {
    if ("outline" in obj) {
      (obj as Polygon | BooleanResult).outline = this._outlineEnabled;
    }

    obj.renderOrder = 0;
    this.applyObjectColor(obj, this.propertySet.color);
    this.forEachMaterial(obj, (material) => {
      material.depthWrite = true;
      material.depthTest = true;
      material.transparent = false;
      material.opacity = 1;
      if ("side" in material) {
        material.side = THREE.DoubleSide;
      }
      material.needsUpdate = true;
    });
  }

  private apply3DRenderStyle(obj: THREE.Object3D): void {
    if ("outline" in obj) {
      (obj as Solid | BooleanResult).outline = this._outlineEnabled;
    }

    obj.renderOrder = 0;
    this.applyObjectColor(obj, this.propertySet.color);
    this.forEachMaterial(obj, (material) => {
      material.depthWrite = true;
      material.depthTest = true;
      material.transparent = false;
      material.opacity = 1;
      if ("side" in material) {
        material.side = THREE.FrontSide;
      }
      material.needsUpdate = true;
    });
  }

  private applyObjectColor(obj: THREE.Object3D, color: number): void {
    if ("color" in obj && typeof (obj as { color?: unknown }).color === "number") {
      (obj as Polygon | Solid).color = color;
    }

    this.forEachMaterial(obj, (material) => {
      if ("color" in material && material.color) {
        material.color.set(color);
      }
    });
  }

  private forEachMaterial(
    obj: THREE.Object3D,
    callback: (material: THREE.Material & { color?: THREE.Color; opacity: number; transparent: boolean; depthWrite: boolean; depthTest: boolean; side?: THREE.Side; needsUpdate: boolean }) => void,
  ): void {
    if (!(obj instanceof THREE.Mesh)) {
      return;
    }

    const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
    materials.forEach((material) => {
      callback(
        material as THREE.Material & {
          color?: THREE.Color;
          opacity: number;
          transparent: boolean;
          depthWrite: boolean;
          depthTest: boolean;
          side?: THREE.Side;
          needsUpdate: boolean;
        },
      );
    });
  }
}
